import React, { useMemo } from "react";
import AcertaEssencialQuery from "./custom-queries-credit/AcertaEssencialQuery";
import LocalizacaoPFQuery from "./custom-queries-credit/LocalizacaoPFQuery";
import DefineRiscoQuery from "./custom-queries-credit/DefineRiscoQuery";
import LocalizacaoPJQuery from "./custom-queries-credit/LocalizacaoPJQuery";
import Protestos from "../common/query/Protestos";
import TitleStripe from "../common/TitleStripe";
import NoOcurrence from "../common/query/NoOcurrence";
import ChequesSemFundos from "../common/query/ChequesSemFundos";
import ScrBacenQuery from "./custom-queries-credit/ScrBacenQuery";
import AcertaEssencialPositivoQuery from "./custom-queries-credit/AcertaEssencialPositivoQuery";
import PendenciasSerasaQuery from "./custom-queries-credit/PendenciasSerasaQuery";
import ScorePFQuery from "./custom-queries-credit/ScorePFQuery";
import ScorePJQuery from "./custom-queries-credit/ScorePJQuery";
import SPCSerasaPJ from "./custom-queries-credit/SPCSerasaPJ";
import SPCSerasaPF from "./custom-queries-credit/SPCSerasaPF";
import CadinQuery from "./custom-queries-credit/CadinQuery";
import DefineLimitePositivoQuery from "./DefineLimitePositivoQuery";
import DefineLimiteQuery from "./DefineLimiteQuery";
import PendenciasQuod from "./custom-queries-credit/PendenciasQuod";
import JudicialProcesses from "./custom-queries-credit/JudicialProcesses";
import DividasQuery from "./custom-queries-credit/DividasQuery";
import CrednetQuery from "./custom-queries-credit/CrednetQuery";
import SerasaPremiumQuery from "./custom-queries-credit/SerasaPremiumQuery";
import PendenciasSPCSerasaPF from "./custom-queries-credit/PendenciasSPCSerasaPF";
import CadastralPFQuery from "./custom-queries-cadastral/CadastralPFQuery";
import CadastralPJQuery from "./custom-queries-cadastral/CadastralPJQuery";
import SCPCQuery from "./custom-queries-credit/SCPCQuery";
import CreditoPreditivo from "./custom-queries-credit/CreditoPreditivo";
import ScoreRating from "./custom-queries-credit/ScoreRating";
import RendaPresumidaPF from "./custom-queries-credit/RendaPresumidaPF";
import GastoEstimadoPF from "./custom-queries-credit/GastoEstimadoPF";
import InformacoesFinanceirasPJQuery from "./custom-queries-credit/InformacoesFinanceirasPJQuery";
import FaturamentoPresumido from "./custom-queries-credit/FaturamentoPresumido";

const FINANCE_KEYS = new Set([
  "recebimentosViaBoletoPJ",
  "pagamentosViaBoletoPJ",
  "predicaoRecebimentoBoletoPJ",
  "concentracaoRecebimentoPJ",
  "concentracaoPagamentoPJ",
  "liquidezPagamentoPJ",
  "liquidezRecebimentoPJ",
  "mediaDiasInadimpleciaPJ",
  "recebimentosViaBoletoCartaoTedPJ",
  "pagamentosViaBoletoTedPJ",
  "scoreEvolucaoPJ",
]);

function coerceAdditionalList(additionalRaw) {
  if (!additionalRaw) return [];
  let add = additionalRaw;
  if (typeof add === "string") {
    try {
      const t = add.trim();
      add = t && t !== "null" && t !== "undefined" ? JSON.parse(t) : null;
    } catch {
      add = null;
    }
  }
  if (!add) return [];
  if (Array.isArray(add)) return add.filter(Boolean);
  if (typeof add === "object") return Object.values(add).filter(Boolean);
  return [];
}

function guessAdditionalKey(item = {}) {
  const tryKeys = [
    item.key,
    item.alias,
    item.nome,
    item.name,
    item.tipo,
    item.type,
  ];
  const first = tryKeys.find(Boolean);
  if (first) return String(first);
  if (item?.scrBacen) return "scrBacen";
  if (item?.consultaCredito?.scoreRating) return "scoreRating";
  return null;
}

function normalizeAdditionalEntries(raw) {
  if (!raw) return [];
  if (typeof raw === "string") {
    return coerceAdditionalList(raw).map((item, idx) => ({
      entryKey: null,
      item,
      idx,
    }));
  }
  if (Array.isArray(raw)) {
    return raw
      .map((item, idx) => ({
        entryKey: null,
        item,
        idx,
      }))
      .filter(({ item }) => Boolean(item));
  }
  if (typeof raw === "object") {
    return Object.entries(raw)
      .map(([entryKey, value], idx) => ({
        entryKey,
        item: value,
        idx,
      }))
      .filter(({ item }) => Boolean(item));
  }
  return [];
}

const CustomCreditQueryAdditional = ({ data }) => {
  const inferredDocument =
    data?.resumoRetorno?.document || data?.document || undefined;

  // Se houver QUALQUER chave financeira ativa no objeto "data", renderizamos o bloco consolidado.
  const anyActiveFinance = useMemo(
    () => Array.from(FINANCE_KEYS).some((k) => data?.[k]?.statusRetorno == 1),
    [data]
  );

  const renderComponentByKey = (key, dataItem, doc) => {
    // Segurança extra caso alguma chave financeira passe pelo filtro por engano.
    if (FINANCE_KEYS.has(key)) {
      return null; // Nunca renderizamos blocos financeiros individuais aqui.
    }

    switch (key) {
      case "cadastralCompleto":
        return <CadastralPFQuery data={dataItem} />;
      case "cadastralCompletoPJ":
        return <CadastralPJQuery data={dataItem} />;
      case "localizacao":
        return doc?.length === 14 ? (
          <LocalizacaoPFQuery data={dataItem} />
        ) : (
          <LocalizacaoPJQuery data={dataItem} />
        );
      case "acertaEssencial":
        return (
          <AcertaEssencialQuery
            data={dataItem?.consultaCredito}
            dataFull={data}
          />
        );
      case "crednet":
        return <CrednetQuery data={dataItem.consultaCredito} />;
      case "serasaPremium":
        return <SerasaPremiumQuery data={dataItem.consultaCredito} />;
      case "acertaEssencialPositivo":
        return (
          <AcertaEssencialPositivoQuery
            data={dataItem?.consultaCredito}
            dataFull={data}
          />
        );
      case "defineRisco":
        return (
          <DefineRiscoQuery data={dataItem?.consultaCredito} dataFull={data} />
        );
      case "defineLimite":
        return <DefineLimiteQuery data={dataItem} dataFull={data} />;
      case "defineLimitePositivo":
        return <DefineLimitePositivoQuery data={dataItem} dataFull={data} />;
      case "spcSerasaPJ":
        return <SPCSerasaPJ data={dataItem} />;
      case "spcSerasaPF":
        return <SPCSerasaPF data={dataItem} />;
      case "pendSPCSerasaPF":
        return <PendenciasSPCSerasaPF data={dataItem} dataFull={data} />;
      case "serasa":
        return <PendenciasSerasaQuery data={dataItem} />;
      case "quod":
        return <PendenciasQuod data={dataItem} />;
      case "faturamentoPresumidoPJ":
        return <FaturamentoPresumido data={dataItem} />;
      case "protestoNacional":
        return (
          <div>
            {dataItem?.statusRetorno == 1 && (
              <>
                <TitleStripe className="mb-4">Protesto Nacional</TitleStripe>
                {dataItem?.ocorrencias?.length > 0 ? (
                  <Protestos
                    quantidadeTotal={
                      dataItem?.resumoConsulta?.protestos?.quantidadeTotal
                    }
                    valorTotal={dataItem?.resumoConsulta?.protestos?.valorTotal}
                    protestos={dataItem.ocorrencias}
                    temVencimento={false}
                  />
                ) : (
                  <NoOcurrence />
                )}
              </>
            )}
          </div>
        );
      case "chequesSemFundo":
        return (
          <div>
            {dataItem?.statusRetorno == 1 && (
              <>
                <TitleStripe>Cheques sem fundo</TitleStripe>
                {dataItem?.ocorrencias?.length > 0 ? (
                  <ChequesSemFundos chequesSemFundos={dataItem.ocorrencias} />
                ) : (
                  <NoOcurrence />
                )}
              </>
            )}
          </div>
        );
      case "scrBacen":
        return (
          <div>
            <ScrBacenQuery data={dataItem.scrBacen} />
          </div>
        );
      case "scorePF":
        return <ScorePFQuery data={dataItem} />;
      case "scorePJ":
        return <ScorePJQuery data={dataItem} />;
      case "cadin":
        return <CadinQuery data={dataItem} />;
      case "acoesProcessos":
        return (
          <div>
            {dataItem?.statusRetorno == 1 && (
              <JudicialProcesses data={dataItem} />
            )}
          </div>
        );
      case "dividas":
        return <DividasQuery data={dataItem} dataFull={data} />;
      case "scpcNet":
        return <SCPCQuery data={dataItem} />;
      case "creditoPreditivo":
        return <CreditoPreditivo data={dataItem} />;
      case "scoreRating":
        return <ScoreRating score={dataItem} data={data} />;
      case "rendaPresumidaPF":
        return <RendaPresumidaPF data={dataItem} />;
      case "gastoEstimadoPF":
        return <GastoEstimadoPF data={dataItem} />;
      default:
        return null;
    }
  };

  const renderSortedData = () => {
    if (!data) return null;

    const sortedItems = normalizeAdditionalEntries(data)
      .map(({ entryKey, item }, index) => {
        const guessedKey = entryKey || guessAdditionalKey(item);
        const key = String(guessedKey ?? `additional_${index + 1}`);
        const payload =
          item?.data != null
            ? item.data
            : item?.consultaCredito != null
            ? item.consultaCredito
            : item;
        const rawPriority = Number(item?.prioridade);
        const prioridade = Number.isFinite(rawPriority)
          ? rawPriority
          : 9999 + index;
        return {
          key,
          prioridade,
          dataItem: payload,
        };
      })
      .sort((a, b) => a.prioridade - b.prioridade);

    if (!sortedItems.length && !anyActiveFinance) return null;

    const hasFinance = sortedItems.some(
      (item) => FINANCE_KEYS.has(item.key) && item.dataItem?.statusRetorno == 1
    );

    const nodes = [];

    if (hasFinance) {
      nodes.push(
        <InformacoesFinanceirasPJQuery
          key="__finance_block__"
          dataFull={data}
        />
      );
    }

    for (const { key, dataItem } of sortedItems) {
      if (FINANCE_KEYS.has(key)) continue;
      nodes.push(
        <React.Fragment key={`${key}_${dataItem?.id ?? key}`}>
          {renderComponentByKey(key, dataItem, inferredDocument)}
        </React.Fragment>
      );
    }

    return nodes;
  };

  return <>{renderSortedData()}</>;
};

export default CustomCreditQueryAdditional;
